import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, User, Phone, Mail, Home, Edit, Plus, Loader2,
  Building, Calendar, FileText, ChevronRight, Save, X
} from "lucide-react";
import { API } from "@/App";

export const VendorDetailPage = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [properties, setProperties] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchVendor();
  }, [vendorId, token]);

  const fetchVendor = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/sales/vendors/${vendorId}`, { headers });
      setVendor(res.data.vendor);
      setProperties(res.data.properties || []);
      setEditForm(res.data.vendor);
    } catch (error) {
      toast.error("Failed to load vendor details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/sales/vendors/${vendorId}`, editForm, { headers });
      toast.success("Vendor updated successfully");
      setVendor(editForm);
      setEditMode(false);
    } catch (error) {
      toast.error("Failed to update vendor");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Vendor not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/sales")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sales
            </Button>
            <div>
              <p className="text-sm text-slate-500">Home &gt; Sales &gt; Vendors &gt; {vendor.name}</p>
              <h1 className="text-2xl font-bold text-purple-700">{vendor.name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editMode ? (
              <Button onClick={() => setEditMode(true)} className="bg-purple-600 hover:bg-purple-700">
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="col-span-2 space-y-6">
            {/* Basic Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Basic Details</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label>Title</Label>
                        <Select value={editForm.title || ""} onValueChange={(v) => setEditForm({...editForm, title: v})}>
                          <SelectTrigger><SelectValue placeholder="Title" /></SelectTrigger>
                          <SelectContent>
                            {["Mr", "Mrs", "Miss", "Ms", "Dr", "Mr & Mrs"].map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>First Name</Label>
                        <Input value={editForm.first_name || ""} onChange={(e) => setEditForm({...editForm, first_name: e.target.value})} />
                      </div>
                      <div>
                        <Label>Last Name</Label>
                        <Input value={editForm.last_name || ""} onChange={(e) => setEditForm({...editForm, last_name: e.target.value})} />
                      </div>
                      <div>
                        <Label>Friendly Name</Label>
                        <Input value={editForm.friendly_name || ""} onChange={(e) => setEditForm({...editForm, friendly_name: e.target.value})} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Company Name</Label>
                        <Input value={editForm.company_name || ""} onChange={(e) => setEditForm({...editForm, company_name: e.target.value})} />
                      </div>
                      <div>
                        <Label>Gender</Label>
                        <Select value={editForm.gender || ""} onValueChange={(v) => setEditForm({...editForm, gender: v})}>
                          <SelectTrigger><SelectValue placeholder="Choose" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Full Name</p>
                      <p className="font-medium">{vendor.name}</p>
                    </div>
                    {vendor.friendly_name && (
                      <div>
                        <p className="text-sm text-slate-500">Friendly Name</p>
                        <p className="font-medium">{vendor.friendly_name}</p>
                      </div>
                    )}
                    {vendor.company_name && (
                      <div>
                        <p className="text-sm text-slate-500">Company</p>
                        <p className="font-medium">{vendor.company_name}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-slate-500">Status</p>
                      <Badge className={vendor.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}>
                        {(vendor.status || "Active").toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contact Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contact Details</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Email</Label>
                      <Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({...editForm, email: e.target.value})} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Mobile Phone</Label>
                        <Input value={editForm.mobile_phone || ""} onChange={(e) => setEditForm({...editForm, mobile_phone: e.target.value})} />
                      </div>
                      <div>
                        <Label>Home Phone</Label>
                        <Input value={editForm.home_phone || ""} onChange={(e) => setEditForm({...editForm, home_phone: e.target.value})} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Work Phone</Label>
                        <Input value={editForm.work_phone || ""} onChange={(e) => setEditForm({...editForm, work_phone: e.target.value})} />
                      </div>
                      <div>
                        <Label>Fax</Label>
                        <Input value={editForm.fax || ""} onChange={(e) => setEditForm({...editForm, fax: e.target.value})} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {vendor.email && (
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <a href={`mailto:${vendor.email}`} className="text-purple-600 hover:underline">{vendor.email}</a>
                      </div>
                    )}
                    {vendor.mobile_phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>Mobile: {vendor.mobile_phone}</span>
                      </div>
                    )}
                    {vendor.home_phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>Home: {vendor.home_phone}</span>
                      </div>
                    )}
                    {vendor.work_phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>Work: {vendor.work_phone}</span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <Textarea 
                    value={editForm.notes || ""} 
                    onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                    rows={4}
                    placeholder="Add notes about this vendor..."
                  />
                ) : (
                  <p className="text-slate-600">{vendor.notes || "No notes"}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Properties */}
          <div className="space-y-6">
            {/* Quick Info */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Date Registered</span>
                    <span className="font-medium">{vendor.created_at ? new Date(vendor.created_at).toLocaleDateString('en-GB') : '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Branch</span>
                    <span className="font-medium">{vendor.branch || "Kempston Office"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Properties</span>
                    <span className="font-medium">{properties.length}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Properties */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Properties</CardTitle>
                <Button 
                  size="sm" 
                  onClick={() => navigate(`/sales/new-instruction?vendor=${vendorId}`)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Property
                </Button>
              </CardHeader>
              <CardContent>
                {properties.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Building className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No properties yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {properties.map((prop, idx) => (
                      <div 
                        key={idx}
                        onClick={() => navigate(`/sales/case/${prop.case_id}`)}
                        className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-800">{prop.property_address || prop.address}</p>
                            <p className="text-sm text-slate-500">£{(prop.price || 0).toLocaleString()}</p>
                          </div>
                          <Badge className={
                            prop.status === "active" ? "bg-green-100 text-green-700" :
                            prop.status === "sstc" ? "bg-blue-100 text-blue-700" :
                            "bg-slate-100 text-slate-700"
                          }>
                            {(prop.status || "Active").toUpperCase()}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDetailPage;
